/**
 * Supabase-backed location data hook with React Query caching.
 * Replaces module-level cache with proper deduplication and stale-while-revalidate.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CountryWithCities {
  id: string;
  name: string;
  flagEmoji: string;
  isoCode: string;
  currencyCode: string;
  cities: { id: string; name: string }[];
}

interface LocationData {
  countryCities: Record<string, string[]>;
  countries: CountryWithCities[];
}

async function fetchLocations(): Promise<LocationData> {
  const [countriesRes, citiesRes] = await Promise.all([
    supabase
      .from("countries")
      .select("id, name, flag_emoji, iso_code, currency_code")
      .eq("is_active", true)
      .order("name"),
    supabase
      .from("cities")
      .select("id, name, country_id")
      .eq("is_active", true)
      .order("name"),
  ]);

  if (countriesRes.error) throw countriesRes.error;
  if (citiesRes.error) throw citiesRes.error;

  const countriesData = countriesRes.data ?? [];
  const citiesData = citiesRes.data ?? [];

  const map: Record<string, string[]> = {};
  const full: CountryWithCities[] = [];

  for (const country of countriesData) {
    const countryCityList = citiesData
      .filter((c) => c.country_id === country.id)
      .map((c) => ({ id: c.id, name: c.name }));

    map[country.name] = countryCityList.map((c) => c.name);
    full.push({
      id: country.id,
      name: country.name,
      flagEmoji: country.flag_emoji,
      isoCode: country.iso_code,
      currencyCode: country.currency_code,
      cities: countryCityList,
    });
  }

  return { countryCities: map, countries: full };
}

export const useLocations = () => {
  const { data, isLoading: loading } = useQuery({
    queryKey: ["locations"],
    queryFn: fetchLocations,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  return {
    countryCities: data?.countryCities ?? {},
    countries: data?.countries ?? [],
    loading,
  };
};

/** Get city ID by name and country name */
export async function getCityId(cityName: string, countryName: string): Promise<string | null> {
  const { data } = await supabase
    .from("cities")
    .select("id, country_id, countries!country_id(name)")
    .eq("name", cityName)
    .limit(10);

  if (!data) return null;
  const match = data.find((c: any) => c.countries?.name === countryName);
  return match?.id || null;
}

/** Get country ID by name */
export async function getCountryId(countryName: string): Promise<string | null> {
  const { data } = await supabase
    .from("countries")
    .select("id")
    .eq("name", countryName)
    .limit(1);

  if (!data || data.length === 0) return null;
  return data[0].id;
}
